/*

Using filters from code:
  this.$options.filters.FILTER_NAME(VALUE)

*/

import Vue from 'vue';
import escapeStringRegexp from 'escape-string-regexp';
import emojiRegex from 'emoji-regex';
import { parsePhoneNumber } from 'libphonenumber-js';
import moment from './momentz';
import { numberFormat, doubleDigit } from './numberFormat';
import store from '@/store';
import getFormat from './lists/countryTimeFormats';
import countryList from '@/helpers/lists/countries';

/**
 * Accepts input as both UTC and UNIX format and creates moment object and returns it.
 *
 * @param value UTC or UNIX time value.
 * @returns Moment object
 */
function parseInputTime(value) {
  let valueMoment;
  if (value.toString().indexOf('-') >= 0) {
    valueMoment = moment(value);
  } else {
    valueMoment = moment(false, false).unix(value);
  }

  return valueMoment;
}

Vue.filter('lowerCase', value => value.toLowerCase());
Vue.filter('trim', value => value.trim());
Vue.filter('digits', value => value.replace(/\D+/g, ''));
Vue.filter('date', value => moment(value).format('YYYY-MM-DD HH:mm:ss'));
Vue.filter('dateWithSlash', value => moment(value).format('DD/MM/YYYY, HH:mm:ss'));
Vue.filter('dateShort', value => moment(value).format('YYYY-MM-DD'));
Vue.filter('dateText', value => moment(value).format('MMM DD, HH:mm'));
Vue.filter('dateMMMDDYYYY', value => moment(value).format('MMM DD, YYYY')); // consider creating a filter which can accept both epoch and unix time. If resulting date id Jan 20, 1970 then input is unix time and has to be multiplied by 100 to get epoch and correct data on the output.
Vue.filter('dateDoMMMMYYYY', value => moment(value).format('Do MMMM YYYY'));
Vue.filter('dateMMMDD', value => moment(value).format('MMM DD'));
Vue.filter('dateMMMMDD', (value, unix) => moment(value, true, unix).format('MMMM DD'));
Vue.filter('hhmmss', value => moment(value).format('HH:mm:ss'));
Vue.filter('moneyFormat', value => numberFormat(value, 2));
Vue.filter('removeUnderscore', value => value.replace(/_/, ' '));
Vue.filter('uppercase', value => value.toUpperCase());
Vue.filter('bigDigits', value => (Number.isFinite(value) ? Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value) : value));

Vue.filter('dateDiff', (value) => {
  const momentDate = moment(false, false).unix(value);
  let returnValue = '';
  if (momentDate.isSame(moment(), 'day')) {
    momentDate.locale(store.getters['user/region']);
    returnValue = momentDate.format('LT');
  } else if (momentDate.isSame(moment(), 'year')) {
    returnValue = momentDate.format(getFormat(store.getters['user/region']).dateShort);
  } else {
    returnValue = momentDate.format(getFormat(store.getters['user/region']).dateFull);
  }
  return returnValue;
});

Vue.filter('tableDate', (value) => {
  const momentDate = moment(false, false).unix(value);
  // momentDate.locale(store.getters['user/region']);
  if (value && momentDate.isValid()) {
    return momentDate.format(getFormat(store.getters['user/region']).dateFull);
    // return momentDate.format('L, LT');
  }
  return '/';
});

/**
 * Replaces underscores with hypens.
 */
Vue.filter('hyphen', value => value.replace(/_/g, '-'));

/**
 * Replaces hypens with underscores.
 */
Vue.filter('underscore', value => value.replace(/-/g, '_'));

/**
 * Replaces underscores with spaces.
 */
Vue.filter('removeUnderscore', value => value.replace(/_/g, ' '));

Vue.filter('noExtension', value => value.replace(/\.([^.]+)$/g, ''));
Vue.filter('extractExtension', value => value.replace(/(.*)(\.([^.]+))$/g, '$2'));

Vue.filter('dateTimeFull', (value) => {
  const momentDate = moment(false, false).unix(value);
  if (value && momentDate.isValid()) {
    const formatedValue = momentDate.format(getFormat(store.getters['user/region']).dateFull);
    momentDate.locale(store.getters['user/region']);
    return `${formatedValue}, ${momentDate.format('LTS')}`;
    // return momentDate.format('L, LT');
  }
  return '/';
});

/**
 * Calendar format based on the provided country code.
 */
Vue.filter('dateCalendar', (value, country) => {
  const momentDate = moment(false, false).unix(value);
  return momentDate.format(getFormat(country).dateCalendar);
});

/**
 * Formats the date for messages and if the date is today it sets 'Today' as date value.
 */
Vue.filter('messagesMainDate', (value) => {
  let timeValue;
  const valueMoment = parseInputTime(value);

  if (valueMoment.isSame(Date.now(), 'day')) {
    timeValue = 'Today';
  } else if (valueMoment.isSame(moment().subtract(1, 'days'), 'day')) {
    timeValue = 'Yesterday';
  } else {
    // const dateVariant = valueMoment.isSame(moment(), 'year') ? 'dateShort' : 'dateFull';
    timeValue = valueMoment.format(getFormat(store.getters['user/region']).dateFull);
  }
  return timeValue;
});

/**
 * Formats the date for messages and if the date is today it sets 'Today' as date value.
 */
Vue.filter('subMessageDate', (value) => {
  const localLocale = moment(false, false).unix(value);
  localLocale.locale(store.getters['user/region']);

  // return moment(false, false).unix(value).format('MMM DD, YYYY h:mm a');
  return localLocale.format('LT');
});

Vue.filter('timeFull', (value) => {
  const localLocale = moment(false, false).unix(value);
  localLocale.locale(store.getters['user/region']);

  // return moment(false, false).unix(value).format('MMM DD, YYYY h:mm a');
  return localLocale.format('LTS');
});

function extractFirstCharacterOrEmoji(text) {
  const emojiRegexInstance = emojiRegex();
  let initials;

  // 1 match emoji
  const foundEmojis = text.match(emojiRegexInstance) || [];
  // 2 check if emoji is first "character"
  if (foundEmojis.length > 0 && RegExp(`${foundEmojis[0]}`).test(text)) {
    initials = foundEmojis[0];
  } else { // 3 if not take the first character
    initials = text.substring(0, 1).toUpperCase();
  }

  return initials;
}

/** Limited to only two characters which is more than enough but it needs a refactor to be clearer. */
Vue.filter('initials', (value, characterCount) => {
  const names = value.split(' ').filter(n => n);
  let initials = names.length ? extractFirstCharacterOrEmoji(names[0]) : '';

  // TODO - only works for two characters. Refactor this so it can output even mora initials- e.g. Carlos Andrade Azevedo ---> CAA
  if (names.length > 1 && characterCount > 1) {
    initials += extractFirstCharacterOrEmoji(names[names.length - 1]);
  }
  return initials;
});

/**
 * Converts seconds into mm:ss format. It returns 00:00 if value is not a number.
 */
Vue.filter('playerTimeFormat', (value) => {
  let minutes = 0;
  let seconds = 0;
  if (Number.isFinite(value)) {
    minutes = Math.floor(value / 60);
    seconds = Math.floor(value) % 60;
  }
  return `${doubleDigit(minutes)}:${doubleDigit(seconds)}`;
});

/**
 * Converts seconds into hh:mm:ss and also trims the leading zeros
 */
Vue.filter('countdownFormat', (value) => {
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  if (Number.isFinite(value)) {
    hours = Math.floor(value / (60 * 60));
    minutes = Math.floor((value / 60) % 60);
    seconds = Math.floor(value) % 60;
  }
  const timeValue = `${doubleDigit(hours)}:${doubleDigit(minutes)}:${doubleDigit(seconds)}`;
  const trimmedValue = timeValue.replace(/^(0+:*)+/, '');
  return trimmedValue === '' ? 0 : trimmedValue;
});

/**
 * Bolds part of the value string that matches to the matchString.
 *
 * TODO - matcher for numbers without parenthesis, dashes, spaces etc.
 */
Vue.filter('boldMatch', (value, matchString) => (matchString ? value.replace(new RegExp(`(${escapeStringRegexp(matchString)})`, 'gi'), '<b class="black">$1</b>') : value));

/**
 * Replaces specific words with "Edit" but only on start of the string
 */
Vue.filter(
  'editMode',
  value => value.replace(/^.*? /, 'Edit '),
);

Vue.filter(
  'phoneFormat',
  (value) => {
    let formatedValue = '';
    try {
      const parsedPhone = parsePhoneNumber(value);
      if (parsedPhone) {
        formatedValue = parsedPhone.formatInternational();
      }
    } catch (error) {
      formatedValue = value;
    }

    return formatedValue;
  },
);
/** Transfers the number to '9+' presentation if the input number is greater than 9. */
Vue.filter(
  'ninePlus',
  (input) => {
    const numberValue = Number.parseInt(input, 10);
    return numberValue && numberValue > 9 ? '9+' : input;
  },
);

// Join these two filters info one which will take input 'max' and as output would give the value or '[max]+'
Vue.filter(
  'nineNinePlus',
  (input) => {
    const numberValue = Number.parseInt(input, 10);
    return numberValue && numberValue > 99 ? '99+' : input;
  },
);

Vue.filter('firstUppercase', (value) => {
  const firstCharacter = value.substring(0, 1).toUpperCase();
  return `${firstCharacter}${value.substring(1)}`;
});

/**
 * Converts country code into country name.
 * e.g. Input "US" outputs as "United States".
 */
Vue.filter('countryName', (value) => {
  const targetCountry = countryList.find(country => country.code === value);
  return targetCountry ? targetCountry.name : 'Unknown country';
});
